import { useEffect } from 'react';
import useOrderbookDisplayStore from './useOrderbookDisplayStore';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import { MarketId, l2ToDisplayBidsAndAsks } from '@velocity-exchange/common';
import { useTickSizeForMarket } from 'src/hooks/useGetTickSizeForMarket';
import { LiquidityType } from '@velocity-exchange/common';
import useDevStore, { DevStore } from '../useDevStore';
import { useDynamicOrderbookDepth } from 'src/components/Orderbook/hooks/useDynamicOrderbookDepth';
import useVelocityStore from '../VelocityStore/useVelocityStore';
import { getMarketPrecision } from 'src/utils/market';
import {
	allOrdersToUserBidsAndAsks,
	DEFAULT_USER_BIDS_AND_ASKS,
} from 'src/hooks/useCurrentUserBidsAndAsks';
import { useCurrentAccountOrders } from 'src/hooks/query/accountTradingData/useOrders';
import { useOrderbookDisplayPreference } from 'src/components/Orderbook/useOrderbookDisplayPreference';
import { ROW_HEIGHT } from 'src/components/Orderbook/OrderbookConstants';
import { MAX_ORDERBOOK_DEPTH } from 'src/components/Orderbook/OrderbookConstants';
import { useL2Orderbook } from 'src/hooks/query/orderbook/useL2Orderbook';

const getDisabledLiquidityTypes = (
	devDlobSettings: DevStore['dlobSettings']
): LiquidityType[] => {
	const disabledLiqTypes: LiquidityType[] = [];

	if (devDlobSettings.showDlob === false) {
		disabledLiqTypes.push('dlob');
	}
	if (devDlobSettings.showVamm === false) {
		disabledLiqTypes.push('vamm');
	}
	if (devDlobSettings.showIndicative === false) {
		disabledLiqTypes.push('indicative');
	}

	return disabledLiqTypes;
};

export const useSyncOrderbookDisplayState = () => {
	const marketId = useOrderbookDisplayStore((s) => s.marketId);
	const grouping = useOrderbookDisplayStore((s) => s.groupingSizeSelection);
	const devDlobSettings = useDevStore((s) => s.dlobSettings);
	const setOrderbookDisplayState = useOrderbookDisplayStore(
		(s) => s.setOrderbookDisplayState
	);

	const { data: l2Orderbook } = useL2Orderbook(marketId);

	const getOraclePriceForMarket = useGetOraclePriceForMarket();
	const tickSize = useTickSizeForMarket(marketId as MarketId);

	const orders = useCurrentAccountOrders();

	const activeMobileView = useVelocityStore((s) => s.activeMobileView);
	const { orderbookDisplayType } = useOrderbookDisplayPreference({
		isMini: activeMobileView === 'trade',
	});
	const { depth: dynamicDepth, containerCallbackRef } =
		useDynamicOrderbookDepth(orderbookDisplayType, ROW_HEIGHT);

	useEffect(() => {
		if (!marketId || !l2Orderbook) {
			return;
		}

		// Price user orders with the oracle the book was priced with, so the
		// user's marker stays on its row as the oracle moves.
		const oraclePrice =
			l2Orderbook.oraclePrice ?? getOraclePriceForMarket(marketId);
		const disabledLiquidityTypes = getDisabledLiquidityTypes(devDlobSettings);
		const basePrecisionToUse = getMarketPrecision(marketId);

		const userBidsAndAsks =
			allOrdersToUserBidsAndAsks(orders, oraclePrice)[marketId.key] ??
			DEFAULT_USER_BIDS_AND_ASKS;

		const displayBidsAndAsks = l2ToDisplayBidsAndAsks(
			l2Orderbook,
			oraclePrice.val,
			tickSize,
			grouping,
			userBidsAndAsks,
			basePrecisionToUse,
			disabledLiquidityTypes,
			MAX_ORDERBOOK_DEPTH,
			dynamicDepth
		);

		setOrderbookDisplayState(
			{
				...displayBidsAndAsks,
				containerCallbackRef,
			},
			userBidsAndAsks
		);
	}, [
		marketId,
		devDlobSettings,
		l2Orderbook,
		tickSize,
		grouping,
		dynamicDepth,
		getOraclePriceForMarket,
		setOrderbookDisplayState,
		containerCallbackRef,
		orders,
	]);
};
